import { db } from './config';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  increment,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

// Testing mode configuration
const TESTING_MODE_KEY = 'affiliate_testing_mode';

// Function to enable/disable testing mode
export const setTestingMode = (enabled: boolean) => {
  localStorage.setItem(TESTING_MODE_KEY, enabled.toString());
};

// Function to check if testing mode is enabled
export const isInTestingMode = (): boolean => {
  const testingMode = localStorage.getItem(TESTING_MODE_KEY);
  return testingMode === 'true';
};

// Helper function to get client IP address (approximation)
export const getClientIPAddress = async (): Promise<string> => {
  try {
    // Try to get IP from a public service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not get IP address from external service, using fallback');
    // Fallback: use a hash of user agent and timestamp as a pseudo-IP
    const fallbackIP = btoa(navigator.userAgent + Date.now().toString()).slice(0, 15);
    return `fallback_${fallbackIP}`;
  }
};

export interface Affiliate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  username: string; // Auto-generated username
  password: string; // Hashed password
  commissionRate: number; // Percentage (e.g., 10 for 10%)
  status: 'active' | 'inactive' | 'pending';
  totalEarnings: number;
  totalLeads: number;
  totalConversions: number;
  pendingEarnings: number; // Earnings not yet paid out
  paidEarnings: number; // Total earnings paid out
  paymentThreshold: number; // Default $1500
  bankDetails?: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    routingNumber?: string;
    swiftCode?: string;
  };
  affiliateLink: string; // Generated affiliate link
  shortUrl: string; // Short URL for easy sharing
  affiliateCode: string; // Human-readable affiliate code
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface AffiliateClick {
  id: string;
  affiliateId: string;
  sessionId: string;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  landingPage: string;
  timestamp: Timestamp;
  converted: boolean;
  conversionType?: 'contact_form' | 'assessment_complete' | 'purchase';
  conversionValue?: number;
  // Enhanced tracking
  timeOnSite?: number; // Time spent on site in seconds
  pagesVisited?: string[]; // Array of pages visited
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  country?: string;
  city?: string;
}

export interface AffiliateConversion {
  id: string;
  affiliateId: string;
  sessionId: string;
  conversionType: 'contact_form' | 'assessment_complete' | 'purchase';
  conversionValue: number;
  commission: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  sourceId?: string; // ID of the source record (e.g., contact form ID)
  adminNotes?: string;
  qualityScore?: number; // 1-10 score for lead quality
  isVerified: boolean; // Admin verification
  isRealBuyer: boolean; // Admin decision on real buyer
  timestamp: Timestamp;
  customerInfo?: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    service?: string;
    message?: string;
  };
  // Enhanced conversion tracking
  clickToConversionTime?: number; // Time from click to conversion in minutes
  totalTimeOnSite?: number; // Total time spent before conversion
  pagesVisitedBeforeConversion?: string[];
  ipAddress?: string; // Added for spam prevention
}

export interface AffiliatePayment {
  id: string;
  affiliateId: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  paymentMethod: 'bank_transfer' | 'paypal' | 'stripe';
  transactionId?: string;
  notes?: string;
  processedAt?: Timestamp;
  createdAt: Timestamp;
}

export interface AffiliateLink {
  id: string;
  affiliateId: string;
  customSlug?: string; // Custom URL slug
  baseUrl: string;
  fullUrl: string;
  shortUrl: string; // Short URL
  clicks: number;
  conversions: number;
  isActive: boolean;
  createdAt: Timestamp;
}

// New interface for affiliate authentication
export interface AffiliateAuth {
  username: string;
  password: string;
}

// New interface for affiliate registration
export interface AffiliateRegistration {
  name: string;
  email: string;
  password: string;
  bankDetails?: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    routingNumber?: string;
    swiftCode?: string;
  };
}

// Enhanced stats interface
export interface AffiliateStats {
  affiliate: Affiliate;
  totalClicks: number;
  totalConversions: number;
  approvedConversions: number;
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  conversionRate: number;
  averageCommission: number;
  monthlyEarnings: number;
  recentConversions: AffiliateConversion[];
  recentClicks: AffiliateClick[];
  pendingConversions: AffiliateConversion[];
  recentPayments: AffiliatePayment[];
  // Enhanced tracking stats
  realBuyers: number;
  fakeLeads: number;
  windowShoppers: number;
  averageTimeOnSite: number;
  topLandingPages: { page: string; clicks: number }[];
  deviceBreakdown: { device: string; count: number }[];
  geographicData: { country: string; count: number }[];
  // Lead tracking data
  assessmentLeads?: any[];
  contactLeads?: any[];
}

// Get affiliate by ID
export const getAffiliate = async (affiliateId: string): Promise<Affiliate | null> => {
  try {
    const affiliateDoc = await getDoc(doc(db, 'affiliates', affiliateId));

    if (affiliateDoc.exists()) {
      return { id: affiliateDoc.id, ...affiliateDoc.data() } as Affiliate;
    }

    return null;
  } catch (error) {
    console.error('Error getting affiliate:', error);
    return null;
  }
};

// Generate unique username (no longer used - using email instead)
// const generateUsername = (name: string): string => {
//   const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
//   const randomSuffix = Math.random().toString(36).substring(2, 6);
//   return `${cleanName}${randomSuffix}`;
// };

// Generate human-readable affiliate identifier
const generateAffiliateCode = (name: string, affiliateId: string): string => {
  // Create a clean, readable code from the affiliate's name
  const cleanName = name.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove special characters
    .substring(0, 8); // Take first 8 characters

  // Add a short unique identifier
  const shortId = affiliateId.substring(0, 4);

  return `${cleanName}${shortId}`;
};

// Generate short URL with human-readable format
const generateShortUrl = (name: string, affiliateId: string): string => {
  const baseUrl = window.location.origin;
  const affiliateCode = generateAffiliateCode(name, affiliateId);
  return `${baseUrl}/partner/${affiliateCode}`;
};

// Hash password (in production, use proper hashing)
const hashPassword = (password: string): string => {
  // This is a simple hash for demo purposes
  // In production, use bcrypt or similar
  return btoa(password);
};

// Create new affiliate with authentication
export const createAffiliate = async (registrationData: AffiliateRegistration): Promise<string> => {
  try {
    const affiliateRef = doc(collection(db, 'affiliates'));
    const affiliateId = affiliateRef.id;
    const baseUrl = window.location.origin;

    // Hash password
    const hashedPassword = hashPassword(registrationData.password);

    // Generate human-readable affiliate links
    const affiliateCode = generateAffiliateCode(registrationData.name, affiliateId);
    const affiliateLink = `${baseUrl}?partner=${affiliateCode}`;
    const shortUrl = generateShortUrl(registrationData.name, affiliateId);

    const newAffiliate: Omit<Affiliate, 'id'> = {
      name: registrationData.name,
      email: registrationData.email,
      phone: '', // Default empty phone
      username: registrationData.email, // Use email as username
      password: hashedPassword,
      commissionRate: 10, // Default 10% commission rate
      status: 'pending',
      totalEarnings: 0,
      totalLeads: 0,
      totalConversions: 0,
      pendingEarnings: 0,
      paidEarnings: 0,
      paymentThreshold: 1500, // Default $1500 threshold
      bankDetails: registrationData.bankDetails || undefined,
      affiliateLink,
      shortUrl,
      affiliateCode,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
    };

    await setDoc(affiliateRef, newAffiliate);
    return affiliateId;
  } catch (error) {
    console.error('Error creating affiliate:', error);
    throw error;
  }
};

// Authenticate affiliate using email
export const authenticateAffiliate = async (email: string, password: string): Promise<Affiliate | null> => {
  try {
    const hashedPassword = hashPassword(password);

    const affiliatesQuery = query(
      collection(db, 'affiliates'),
      where('email', '==', email),
      where('password', '==', hashedPassword)
    );

    const snapshot = await getDocs(affiliatesQuery);
    if (!snapshot.empty) {
      const affiliateDoc = snapshot.docs[0];
      return { id: affiliateDoc.id, ...affiliateDoc.data() } as Affiliate;
    }

    return null;
  } catch (error) {
    console.error('Error authenticating affiliate:', error);
    return null;
  }
};

// Get affiliate by email
export const getAffiliateByEmail = async (email: string): Promise<Affiliate | null> => {
  try {
    const affiliatesQuery = query(
      collection(db, 'affiliates'),
      where('email', '==', email)
    );

    const snapshot = await getDocs(affiliatesQuery);
    if (!snapshot.empty) {
      const affiliateDoc = snapshot.docs[0];
      return { id: affiliateDoc.id, ...affiliateDoc.data() } as Affiliate;
    }

    return null;
  } catch (error) {
    console.error('Error getting affiliate by email:', error);
    return null;
  }
};

// Enhanced security: Generate a unique tracking token that can't be easily manipulated
export const generateSecureTrackingToken = (affiliateId: string, sessionId: string): string => {
  const timestamp = Date.now();
  const data = `${affiliateId}:${sessionId}:${timestamp}`;

  // Create a simple hash (in production, use a proper cryptographic hash)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `${affiliateId}_${timestamp}_${Math.abs(hash).toString(36)}`;
};

// Enhanced security: Validate tracking token on server side
export const validateTrackingToken = (token: string, affiliateId: string, sessionId: string): boolean => {
  try {
    const parts = token.split('_');
    if (parts.length !== 3) return false;

    const [tokenAffiliateId, timestamp, hash] = parts;

    // Check if affiliate ID matches
    if (tokenAffiliateId !== affiliateId) return false;

    // Check if token is not too old (24 hours)
    const tokenTime = parseInt(timestamp);
    const now = Date.now();
    if (now - tokenTime > 24 * 60 * 60 * 1000) return false;

    // Recreate hash to validate
    const data = `${affiliateId}:${sessionId}:${timestamp}`;
    let expectedHash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      expectedHash = ((expectedHash << 5) - expectedHash) + char;
      expectedHash = expectedHash & expectedHash;
    }

    return Math.abs(expectedHash).toString(36) === hash;
  } catch (error) {
    console.error('Error validating tracking token:', error);
    return false;
  }
};

// Enhanced security: Store tracking state with additional validation
export const storeSecureAffiliateSession = (affiliateId: string, sessionId: string, trackingToken: string): void => {
  try {
    const sessionData = {
      affiliateId,
      sessionId,
      trackingToken,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      fingerprint: generateBrowserFingerprint(),
    };

    // Use an obfuscated key for the session data
    let hash = 0;
    for (let i = 0; i < affiliateId.length; i++) {
      const char = affiliateId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    const sessionKey = `user_${Math.abs(hash).toString(36)}_session`;

    // Store the session data
    const sessionDataString = JSON.stringify(sessionData);
    localStorage.setItem(sessionKey, sessionDataString);

    // Also set the session flag
    localStorage.setItem('affiliate_session_active', 'true');
  } catch (error) {
    console.error('Error storing session:', error);
  }
};

// Update affiliate details
export const updateAffiliate = async (affiliateId: string, data: Partial<Affiliate>): Promise<void> => {
  try {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    await updateDoc(affiliateRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating affiliate:', error);
    throw error;
  }
};

// Enhanced security: Generate browser fingerprint for additional validation
export const generateBrowserFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx?.fillText('Browser fingerprint', 10, 10);

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency,
    canvas.toDataURL().substring(0, 50), // Canvas fingerprint
  ].join('|');

  // Simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(36);
};

// Clear affiliate session (for logout or session expiry)
export const clearAffiliateSession = (): void => {
  try {
    // Remove session flag
    localStorage.removeItem('affiliate_session_active');

    // Remove session data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_') && key.endsWith('_session')) {
        localStorage.removeItem(key);
        break;
      }
    }
  } catch (error) {
    console.error('Error clearing affiliate session:', error);
  }
};



// Enhanced security: Get affiliate session with validation
export const getSecureAffiliateSession = (): {
  affiliateId: string;
  sessionId: string;
  trackingToken: string;
  timestamp: number;
  userAgent: string;
  fingerprint: string;
} | null => {
  try {
    // First check if we have an active session flag
    const sessionActive = localStorage.getItem('affiliate_session_active');
    if (!sessionActive) {
      return null;
    }

    // Look for any session data in localStorage
    let sessionData = null;
    let sessionKey = null;

    // First, try to find the session key more efficiently
    const sessionKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_') && key.endsWith('_session')) {
        sessionKeys.push(key);
      }
    }

    // Try each session key
    for (const key of sessionKeys) {
      const session = localStorage.getItem(key);
      if (session) {
        try {
          const parsed = JSON.parse(session);
          if (parsed.affiliateId && parsed.sessionId) {
            sessionData = parsed;
            sessionKey = key;
            break;
          }
        } catch (e) {
          // Invalid JSON, continue to next key
        }
      }
    }

    if (!sessionData) {
      localStorage.removeItem('affiliate_session_active');
      return null;
    }

    // Check if session is less than 24 hours old
    if (Date.now() - sessionData.timestamp > 24 * 60 * 60 * 1000) {
      if (sessionKey) {
        localStorage.removeItem(sessionKey);
      }
      localStorage.removeItem('affiliate_session_active');
      return null;
    }

    // Validate browser fingerprint hasn't changed significantly
    const currentFingerprint = generateBrowserFingerprint();
    if (sessionData.fingerprint && Math.abs(currentFingerprint.length - sessionData.fingerprint.length) > 10) {
      if (sessionKey) {
        localStorage.removeItem(sessionKey);
      }
      localStorage.removeItem('affiliate_session_active');
      return null;
    }

    return sessionData;
  } catch (error) {
    console.error('Error getting secure affiliate session:', error);
    return null;
  }
};

// Track affiliate click with enhanced spam prevention
export const trackAffiliateClick = async (affiliateId: string, sessionId: string, clickData: Partial<AffiliateClick> & { trackingToken?: string }): Promise<string> => {
  try {
    // Enhanced security: Validate tracking token if provided
    if (clickData.trackingToken && !validateTrackingToken(clickData.trackingToken, affiliateId, sessionId)) {
      console.log('🚫 Invalid tracking token detected, but continuing for testing...');
      // throw new Error('Invalid tracking token');
    }

    // Get IP address (this would ideally be done server-side, but we'll use a client-side approximation)
    const ipAddress = await getClientIPAddress();

    // Skip spam prevention checks if in testing mode
    if (!isInTestingMode()) {
      // Check for duplicate clicks from same IP within the last hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const duplicateCheckQuery = query(
        collection(db, 'affiliate_clicks'),
        where('affiliateId', '==', affiliateId),
        where('ipAddress', '==', ipAddress),
        where('timestamp', '>=', oneHourAgo)
      );

      const duplicateSnapshot = await getDocs(duplicateCheckQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error('Duplicate click detected from same IP address within 1 hour');
      }

      // Check for suspicious activity (too many clicks from same IP in 24 hours)
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const suspiciousActivityQuery = query(
        collection(db, 'affiliate_clicks'),
        where('ipAddress', '==', ipAddress),
        where('timestamp', '>=', twentyFourHoursAgo)
      );

      const suspiciousSnapshot = await getDocs(suspiciousActivityQuery);

      if (suspiciousSnapshot.size >= 10) {
        throw new Error('Suspicious activity detected: Too many clicks from same IP address');
      }
    }

    const clickRef = doc(collection(db, 'affiliate_clicks'));

    // Detect device type
    const userAgent = navigator.userAgent;
    let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      deviceType = /iPad/.test(userAgent) ? 'tablet' : 'mobile';
    }

    const newClick: any = {
      affiliateId,
      sessionId,
      converted: false,
      timestamp: serverTimestamp() as Timestamp,
      landingPage: window.location.pathname,
      ipAddress,
      userAgent,
      referrer: document.referrer,
      deviceType,
    };

    // Only add optional fields if they have values
    if (clickData.country) {
      newClick.country = clickData.country;
    }
    if (clickData.city) {
      newClick.city = clickData.city;
    }
    if (clickData.timeOnSite) {
      newClick.timeOnSite = clickData.timeOnSite;
    }
    if (clickData.pagesVisited) {
      newClick.pagesVisited = clickData.pagesVisited;
    }

    await setDoc(clickRef, newClick);

    // Update affiliate stats
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      totalLeads: increment(1),
      updatedAt: serverTimestamp(),
    });

    return clickRef.id;
  } catch (error) {
    console.error('Error tracking affiliate click:', error);
    throw error;
  }
};

// Record conversion with enhanced tracking and spam prevention
export const recordConversion = async (
  affiliateId: string,
  sessionId: string,
  conversionType: AffiliateConversion['conversionType'],
  conversionValue: number,
  customerInfo?: AffiliateConversion['customerInfo'],
  trackingToken?: string // Add tracking token for validation
): Promise<string> => {
  try {
    // Enhanced security: Validate tracking token if provided
    if (trackingToken && !validateTrackingToken(trackingToken, affiliateId, sessionId)) {
      console.log('🚫 Invalid tracking token detected for conversion');
      throw new Error('Invalid tracking token for conversion');
    }

    // Check for existing conversion with same session and type to prevent duplicates
    const existingConversionQuery = query(
      collection(db, 'affiliate_conversions'),
      where('affiliateId', '==', affiliateId),
      where('sessionId', '==', sessionId),
      where('conversionType', '==', conversionType)
    );

    const existingConversionSnapshot = await getDocs(existingConversionQuery);

    if (!existingConversionSnapshot.empty) {
      // Return the existing conversion ID instead of creating a new one
      const existingConversion = existingConversionSnapshot.docs[0];
      return existingConversion.id;
    }

    // Get IP address for spam prevention
    const ipAddress = await getClientIPAddress();

    // Skip spam prevention checks if in testing mode
    if (!isInTestingMode()) {
      // Check for duplicate conversions from same IP within the last 24 hours
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const duplicateConversionQuery = query(
        collection(db, 'affiliate_conversions'),
        where('affiliateId', '==', affiliateId),
        where('ipAddress', '==', ipAddress),
        where('timestamp', '>=', twentyFourHoursAgo)
      );

      const duplicateSnapshot = await getDocs(duplicateConversionQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error('Duplicate conversion detected from same IP address within 24 hours');
      }

      // Check for suspicious conversion activity (too many conversions from same IP in 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const suspiciousConversionQuery = query(
        collection(db, 'affiliate_conversions'),
        where('ipAddress', '==', ipAddress),
        where('timestamp', '>=', sevenDaysAgo)
      );

      const suspiciousSnapshot = await getDocs(suspiciousConversionQuery);

      if (suspiciousSnapshot.size >= 5) {
        throw new Error('Suspicious conversion activity detected: Too many conversions from same IP address');
      }
    }
    // Get affiliate to calculate commission
    const affiliate = await getAffiliate(affiliateId);
    if (!affiliate) {
      throw new Error('Affiliate not found');
    }

    const commission = (conversionValue * affiliate.commissionRate) / 100;

    // Get click data to calculate time metrics
    const clicksQuery = query(
      collection(db, 'affiliate_clicks'),
      where('affiliateId', '==', affiliateId),
      where('sessionId', '==', sessionId)
    );
    const clicksSnapshot = await getDocs(clicksQuery);
    let clickToConversionTime: number | undefined;
    let totalTimeOnSite: number | undefined;
    let pagesVisitedBeforeConversion: string[] | undefined;

    if (!clicksSnapshot.empty) {
      const clickDoc = clicksSnapshot.docs[0];
      const clickData = clickDoc.data() as AffiliateClick;
      const clickTime = clickData.timestamp.toDate();
      const conversionTime = new Date();
      clickToConversionTime = Math.round((conversionTime.getTime() - clickTime.getTime()) / (1000 * 60)); // minutes

      // Update click record as converted
      await updateDoc(clickDoc.ref, {
        converted: true,
        conversionType,
        conversionValue,
        timeOnSite: clickToConversionTime * 60, // Convert to seconds
      });
    }

    // Create conversion record
    const conversionRef = doc(collection(db, 'affiliate_conversions'));
    const newConversion: any = {
      affiliateId,
      sessionId,
      conversionType,
      conversionValue,
      commission,
      status: 'pending',
      isVerified: false, // Requires admin verification
      isRealBuyer: false, // Admin will decide
      timestamp: serverTimestamp() as Timestamp,
      customerInfo,
      ipAddress, // Add IP address for spam prevention
    };

    // Only add optional fields if they have values
    if (clickToConversionTime !== undefined) {
      newConversion.clickToConversionTime = clickToConversionTime;
    }
    if (totalTimeOnSite !== undefined) {
      newConversion.totalTimeOnSite = totalTimeOnSite;
    }
    if (pagesVisitedBeforeConversion !== undefined) {
      newConversion.pagesVisitedBeforeConversion = pagesVisitedBeforeConversion;
    }

    await setDoc(conversionRef, newConversion);

    // Update affiliate stats (pending until approved)
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      totalConversions: increment(1),
      updatedAt: serverTimestamp(),
    });

    console.log(`✅ Conversion recorded successfully ${isInTestingMode() ? '(TESTING MODE)' : 'with spam prevention'}:`, {
      affiliateId,
      conversionType,
      conversionValue,
      commission,
      ipAddress,
      timestamp: new Date().toISOString()
    });

    return conversionRef.id;
  } catch (error) {
    console.error('Error recording conversion:', error);
    throw error;
  }
};

// Get affiliate statistics with enhanced analytics and lead tracking
export const getAffiliateStats = async (affiliateId: string): Promise<AffiliateStats | null> => {
  try {
    const affiliate = await getAffiliate(affiliateId);

    if (!affiliate) {
      return null;
    }

    // Get all clicks with error handling
    let allClicks: AffiliateClick[] = [];
    try {
      const clicksQuery = query(
        collection(db, 'affiliate_clicks'),
        where('affiliateId', '==', affiliateId)
      );
      const clicksSnapshot = await getDocs(clicksQuery);
      allClicks = clicksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AffiliateClick[];
    } catch (error) {
      console.warn('Error fetching clicks:', error);
      allClicks = [];
    }

    // Get all conversions with error handling
    let allConversions: AffiliateConversion[] = [];
    try {
      const conversionsQuery = query(
        collection(db, 'affiliate_conversions'),
        where('affiliateId', '==', affiliateId)
      );
      const conversionsSnapshot = await getDocs(conversionsQuery);
      allConversions = conversionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AffiliateConversion[];
    } catch (error) {
      console.warn('Error fetching conversions:', error);
      allConversions = [];
    }

    // Get leads from assessments and contact forms
    let assessmentLeads: any[] = [];
    let contactLeads: any[] = [];
    try {
      // Get assessment leads
      const assessmentsQuery = query(
        collection(db, 'mw-portfolio-assessments'),
        where('affiliateRef.affiliateId', '==', affiliateId)
      );
      const assessmentsSnapshot = await getDocs(assessmentsQuery);
      assessmentLeads = assessmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get contact form leads
      const contactsQuery = query(
        collection(db, 'contactForms'),
        where('affiliateRef.affiliateId', '==', affiliateId)
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      contactLeads = contactsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.warn('Error fetching leads:', error);
    }

    // Get all conversions for the table (not just recent ones)
    const recentConversions = allConversions;

    // Get recent clicks (last 30 days)
    const recentClicks = allClicks.filter(click =>
      click.timestamp.toDate() >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );

    // Get pending conversions
    const pendingConversions = allConversions.filter(conv => conv.status === 'pending');

    // Get recent payments with error handling
    let recentPayments: AffiliatePayment[] = [];
    try {
      // First try with date filter
      const paymentsQuery = query(
        collection(db, 'affiliate_payments'),
        where('affiliateId', '==', affiliateId),
        where('createdAt', '>=', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000))
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      recentPayments = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AffiliatePayment[];
    } catch (error) {
      console.warn('Error fetching payments with date filter, trying without filter:', error);
      try {
        // Fallback: get all payments for this affiliate
        const paymentsQuery = query(
          collection(db, 'affiliate_payments'),
          where('affiliateId', '==', affiliateId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const allPayments = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AffiliatePayment[];

        recentPayments = allPayments.filter(payment => {
          // Filter client-side for recent payments (last 90 days)
          const paymentDate = payment.createdAt.toDate();
          const cutoffDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
          return paymentDate >= cutoffDate;
        });
      } catch (fallbackError) {
        console.warn('Error fetching payments without date filter:', fallbackError);
        recentPayments = [];
      }
    }

    // Calculate enhanced analytics including leads
    const totalClicks = allClicks.length;
    const totalConversions = allConversions.length;

    // For affiliate dashboard, only count affiliate conversions, not leads
    // Leads are managed separately in admin panel
    const totalConversionsWithLeads = totalConversions;

    const conversionRate = totalClicks > 0 ? (totalConversionsWithLeads / totalClicks) * 100 : 0;
    const averageCommission = totalConversions > 0 ?
      allConversions.reduce((sum, conv) => sum + conv.commission, 0) / totalConversions : 0;

    // Sophisticated lead analysis - only count affiliate conversions
    // Leads are managed separately in admin panel
    let realBuyers = allConversions.filter(conv => conv.isRealBuyer && conv.status === 'approved').length;
    let fakeLeads = allConversions.filter(conv => conv.status === 'rejected').length;
    let windowShoppers = allConversions.filter(conv =>
      conv.status === 'approved' && !conv.isRealBuyer
    ).length;

    // Time on site analysis
    const timeOnSiteData = allClicks.filter(click => click.timeOnSite).map(click => click.timeOnSite!);
    const averageTimeOnSite = timeOnSiteData.length > 0 ?
      timeOnSiteData.reduce((sum, time) => sum + time, 0) / timeOnSiteData.length : 0;

    // Top landing pages
    const pageCounts: { [key: string]: number } = {};
    allClicks.forEach(click => {
      pageCounts[click.landingPage] = (pageCounts[click.landingPage] || 0) + 1;
    });
    const topLandingPages = Object.entries(pageCounts)
      .map(([page, clicks]) => ({ page, clicks }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    // Device breakdown
    const deviceCounts: { [key: string]: number } = {};
    allClicks.forEach(click => {
      if (click.deviceType) {
        deviceCounts[click.deviceType] = (deviceCounts[click.deviceType] || 0) + 1;
      }
    });
    const deviceBreakdown = Object.entries(deviceCounts)
      .map(([device, count]) => ({ device, count }));

    // Geographic data
    const geoCounts: { [key: string]: number } = {};
    allClicks.forEach(click => {
      if (click.country) {
        geoCounts[click.country] = (geoCounts[click.country] || 0) + 1;
      }
    });
    const geographicData = Object.entries(geoCounts)
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Calculate approved/paid earnings (actual earnings)
    const approvedConversions = allConversions.filter(conv => conv.status === 'approved' || conv.status === 'paid');
    const approvedEarnings = approvedConversions.reduce((sum, conv) => sum + conv.commission, 0);

    return {
      affiliate,
      totalClicks,
      totalConversions: totalConversionsWithLeads,
      approvedConversions: approvedConversions.length,
      totalEarnings: approvedEarnings, // Use approved earnings for accuracy on the dashboard
      pendingEarnings: affiliate.pendingEarnings,
      paidEarnings: affiliate.paidEarnings,
      conversionRate,
      averageCommission,
      monthlyEarnings: recentConversions
        .filter(conv => conv.status === 'approved' || conv.status === 'paid')
        .reduce((sum, conv) => sum + conv.commission, 0),
      recentConversions,
      recentClicks,
      pendingConversions,
      recentPayments,
      realBuyers,
      fakeLeads,
      windowShoppers,
      averageTimeOnSite,
      topLandingPages,
      deviceBreakdown,
      geographicData,
      // Add lead data for dashboard display
      assessmentLeads,
      contactLeads,
    };
  } catch (error) {
    console.error('Error getting affiliate stats:', error);
    return null;
  }
};

// Get all affiliates (admin function)
export const getAllAffiliates = async (): Promise<Affiliate[]> => {
  try {
    const affiliatesSnapshot = await getDocs(collection(db, 'affiliates'));
    return affiliatesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Affiliate[];
  } catch (error) {
    console.error('Error getting all affiliates:', error);
    return [];
  }
};

// Get all pending conversions (admin function)
export const getPendingConversions = async (): Promise<AffiliateConversion[]> => {
  try {
    const conversionsQuery = query(
      collection(db, 'affiliate_conversions'),
      where('status', '==', 'pending')
    );
    const conversionsSnapshot = await getDocs(conversionsQuery);

    return conversionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AffiliateConversion[];
  } catch (error) {
    console.error('Error getting pending conversions:', error);
    return [];
  }
};

// Get all conversions (admin function) - for comprehensive stats
export const getAllConversions = async (): Promise<AffiliateConversion[]> => {
  try {
    const conversionsSnapshot = await getDocs(collection(db, 'affiliate_conversions'));

    return conversionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AffiliateConversion[];
  } catch (error) {
    console.error('Error getting all conversions:', error);
    return [];
  }
};

// Get all pending payments (admin function)
export const getPendingPayments = async (): Promise<AffiliatePayment[]> => {
  try {
    const paymentsQuery = query(
      collection(db, 'affiliate_payments'),
      where('status', '==', 'pending')
    );
    const paymentsSnapshot = await getDocs(paymentsQuery);

    return paymentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AffiliatePayment[];
  } catch (error) {
    console.error('Error getting pending payments:', error);
    return [];
  }
};

// Update affiliate status
export const updateAffiliateStatus = async (affiliateId: string, status: Affiliate['status']): Promise<void> => {
  try {
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      status,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating affiliate status:', error);
    throw error;
  }
};

// Delete affiliate and all related data
export const deleteAffiliate = async (affiliateId: string): Promise<void> => {
  try {
    const batch = writeBatch(db);

    // Delete affiliate document
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    batch.delete(affiliateRef);

    // Delete all affiliate clicks
    const clicksQuery = query(
      collection(db, 'affiliate_clicks'),
      where('affiliateId', '==', affiliateId)
    );
    const clicksSnapshot = await getDocs(clicksQuery);
    clicksSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete all affiliate conversions
    const conversionsQuery = query(
      collection(db, 'affiliate_conversions'),
      where('affiliateId', '==', affiliateId)
    );
    const conversionsSnapshot = await getDocs(conversionsQuery);
    conversionsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete all affiliate payments
    const paymentsQuery = query(
      collection(db, 'affiliate_payments'),
      where('affiliateId', '==', affiliateId)
    );
    const paymentsSnapshot = await getDocs(paymentsQuery);
    paymentsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Commit all deletions
    await batch.commit();
  } catch (error) {
    console.error('Error deleting affiliate:', error);
    throw error;
  }
};

// Approve or reject conversion (admin function)
export const approveConversion = async (
  conversionId: string,
  status: 'approved' | 'rejected',
  adminNotes?: string,
  qualityScore?: number,
  isRealBuyer: boolean = false,
  projectCost?: number
): Promise<void> => {
  try {
    const conversionRef = doc(db, 'affiliate_conversions', conversionId);
    const conversionDoc = await getDoc(conversionRef);

    if (!conversionDoc.exists()) {
      throw new Error('Conversion not found');
    }

    const conversionData = conversionDoc.data() as AffiliateConversion;

    // Calculate new commission if project cost is provided
    let finalCommission = conversionData.commission;
    let finalConversionValue = conversionData.conversionValue;

    if (projectCost && projectCost > 0) {
      finalConversionValue = projectCost;
      finalCommission = projectCost * 0.1; // 10% commission rate
    }

    await updateDoc(conversionRef, {
      status,
      adminNotes,
      qualityScore,
      isVerified: true,
      isRealBuyer,
      conversionValue: finalConversionValue,
      commission: finalCommission,
      projectCost: projectCost || conversionData.conversionValue,
      updatedAt: serverTimestamp(),
    });

    // If approved and is real buyer, update affiliate earnings
    if (status === 'approved' && isRealBuyer) {
      await updateDoc(doc(db, 'affiliates', conversionData.affiliateId), {
        totalEarnings: increment(finalCommission),
        pendingEarnings: increment(finalCommission),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error approving conversion:', error);
    throw error;
  }
};

// Process payment when threshold is reached
export const processPayment = async (affiliateId: string): Promise<string> => {
  try {
    const affiliate = await getAffiliate(affiliateId);
    if (!affiliate) {
      throw new Error('Affiliate not found');
    }

    if (affiliate.pendingEarnings < affiliate.paymentThreshold) {
      throw new Error('Payment threshold not reached');
    }

    if (!affiliate.bankDetails) {
      throw new Error('Bank details not provided');
    }

    // Create payment record
    const paymentRef = doc(collection(db, 'affiliate_payments'));
    const newPayment: Omit<AffiliatePayment, 'id'> = {
      affiliateId,
      amount: affiliate.pendingEarnings,
      status: 'pending',
      paymentMethod: 'bank_transfer',
      createdAt: serverTimestamp() as Timestamp,
    };

    await setDoc(paymentRef, newPayment);

    // Update affiliate stats
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      pendingEarnings: 0,
      paidEarnings: increment(affiliate.pendingEarnings),
      updatedAt: serverTimestamp(),
    });

    // Mark conversions as paid
    const conversionsQuery = query(
      collection(db, 'affiliate_conversions'),
      where('affiliateId', '==', affiliateId),
      where('status', '==', 'approved')
    );
    const conversionsSnapshot = await getDocs(conversionsQuery);

    const batch = writeBatch(db);
    conversionsSnapshot.docs.forEach(doc => {
      batch.update(doc.ref, { status: 'paid' });
    });
    await batch.commit();

    return paymentRef.id;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

// Update affiliate bank details
export const updateBankDetails = async (
  affiliateId: string,
  bankDetails: Affiliate['bankDetails']
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      bankDetails,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating bank details:', error);
    throw error;
  }
};

// Update payment threshold
export const updatePaymentThreshold = async (
  affiliateId: string,
  threshold: number
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      paymentThreshold: threshold,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating payment threshold:', error);
    throw error;
  }
};

// Sync affiliate total earnings with actual conversion data (admin function)
export const syncAffiliateEarnings = async (affiliateId: string): Promise<void> => {
  try {
    // Get all approved/paid conversions for this affiliate
    const conversionsQuery = query(
      collection(db, 'affiliate_conversions'),
      where('affiliateId', '==', affiliateId),
      where('status', 'in', ['approved', 'paid'])
    );
    const conversionsSnapshot = await getDocs(conversionsQuery);

    // Calculate total earnings from conversions
    const totalEarnings = conversionsSnapshot.docs.reduce((sum, doc) => {
      const conversion = doc.data() as AffiliateConversion;
      return sum + conversion.commission;
    }, 0);

    // Update affiliate record with correct total earnings
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      totalEarnings,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error syncing affiliate earnings:', error);
    throw error;
  }
};

// Generate unique session ID
export const generateSessionId = (): string => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get affiliate code from URL parameters or path
export const getAffiliateFromUrl = (): string | null => {
  const pathname = window.location.pathname;

  // First check for query parameter (e.g., ?partner=imranjavcWiA)
  const urlParams = new URLSearchParams(window.location.search);
  const partnerParam = urlParams.get('partner');
  if (partnerParam) {
    return partnerParam;
  }

  // Then check for path-based affiliate code (e.g., /partner/imranjavcWiA or /affiliate-portal/imranjavcWiA)
  const partnerPathMatch = pathname.match(/^\/partner\/([^/]+)$/);
  if (partnerPathMatch) {
    return partnerPathMatch[1];
  }

  // Check for affiliate-portal path
  const affiliatePortalMatch = pathname.match(/^\/affiliate-portal\/([^/]+)$/);
  if (affiliatePortalMatch) {
    return affiliatePortalMatch[1];
  }

  return null;
};

// Get affiliate ID from affiliate code
export const getAffiliateIdFromCode = async (affiliateCode: string): Promise<string | null> => {
  try {
    // Query affiliates to find the one with matching code
    const affiliatesQuery = query(
      collection(db, 'affiliates'),
      where('affiliateCode', '==', affiliateCode)
    );

    const snapshot = await getDocs(affiliatesQuery);
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }

    return null;
  } catch (error) {
    console.error('Error getting affiliate ID from code:', error);
    return null;
  }
};



// Store affiliate session in localStorage
export const storeAffiliateSession = (affiliateId: string, sessionId: string): void => {
  localStorage.setItem('affiliate_session', JSON.stringify({
    affiliateId,
    sessionId,
    timestamp: Date.now(),
  }));
};

// Get affiliate session from localStorage
export const getAffiliateSession = (): { affiliateId: string; sessionId: string; timestamp: number } | null => {
  const session = localStorage.getItem('affiliate_session');
  if (session) {
    const sessionData = JSON.parse(session);
    // Check if session is less than 24 hours old
    if (Date.now() - sessionData.timestamp < 24 * 60 * 60 * 1000) {
      return sessionData;
    }
  }
  return null;
};

// Test function to add sample affiliate data (for development/testing only)
export const addTestAffiliateData = async (affiliateId: string): Promise<void> => {
  try {
    // Update affiliate stats
    await updateDoc(doc(db, 'affiliates', affiliateId), {
      totalLeads: increment(1),
      totalConversions: increment(1),
      totalEarnings: increment(5),
      pendingEarnings: increment(5),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding test affiliate data:', error);
    throw error;
  }
};

// Test function to manually create a session for debugging
export const testCreateSession = (affiliateId: string): void => {
  try {
    const sessionId = generateSessionId();
    const trackingToken = generateSecureTrackingToken(affiliateId, sessionId);

    // Store the session
    storeSecureAffiliateSession(affiliateId, sessionId, trackingToken);

    // Immediately try to retrieve it
    getSecureAffiliateSession();
  } catch (error) {
    console.error('Error in session test:', error);
  }
};



// Test function to create a test affiliate (for debugging)
export const createTestAffiliate = async (): Promise<string> => {
  try {
    console.log('🧪 Creating test affiliate...');

    const testAffiliate = {
      name: 'Test Affiliate',
      email: 'test@example.com',
      phone: '+1234567890',
      username: 'testaffiliate',
      password: 'hashedpassword123',
      commissionRate: 10,
      status: 'active',
      totalEarnings: 0,
      totalLeads: 0,
      totalConversions: 0,
      pendingEarnings: 0,
      paidEarnings: 0,
      paymentThreshold: 1500,
      affiliateLink: 'https://example.com/?partner=imranjavcWiA',
      shortUrl: 'https://short.url/imranjavcWiA',
      affiliateCode: 'imranjavcWiA',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const affiliateRef = doc(collection(db, 'affiliates'));
    await setDoc(affiliateRef, testAffiliate);

    return affiliateRef.id;
  } catch (error) {
    console.error('❌ Error creating test affiliate:', error);
    throw error;
  }
};

// Global function for console testing - creates session for specific affiliate
export const createSessionForCurrentAffiliate = (affiliateId: string): void => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    // Enable testing mode
    setTestingMode(true);

    // Generate session data
    const sessionId = generateSessionId();
    const trackingToken = generateSecureTrackingToken(affiliateId, sessionId);

    // Store the session
    storeSecureAffiliateSession(affiliateId, sessionId, trackingToken);
  } catch (error) {
    console.error('Error creating testing session:', error);
  }
};

// Clear testing session
export const clearTestingSession = (): void => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    console.log('🚫 Testing functions are only available in development mode');
    return;
  }

  try {
    console.log('🧪 Clearing testing session...');

    // Clear session data
    localStorage.removeItem('affiliate_session');
    localStorage.removeItem('affiliate_tracking_token');
    localStorage.removeItem('affiliate_id');

    // Disable testing mode
    setTestingMode(false);

    console.log('✅ Testing session cleared successfully');
  } catch (error) {
    console.error('❌ Error clearing testing session:', error);
  }
};

// Check testing status
export const checkTestingStatus = (): void => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    console.log('🚫 Testing functions are only available in development mode');
    return;
  }

  try {
    const session = getSecureAffiliateSession();
    const isTesting = isInTestingMode();

    console.log('🧪 Testing Status:');
    console.log('  - Testing Mode:', isTesting ? 'ENABLED' : 'DISABLED');
    console.log('  - Session ID:', session?.sessionId || 'None');
    console.log('  - Affiliate ID:', session?.affiliateId || 'None');
    console.log('  - Tracking Token:', session?.trackingToken || 'None');
  } catch (error) {
    console.error('❌ Error checking testing status:', error);
  }
};

// Clear all affiliate data (for testing)
export const clearAllAffiliateData = async (): Promise<void> => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    console.log('🚫 Testing functions are only available in development mode');
    return;
  }

  try {
    console.log('🧪 Clearing all affiliate data...');

    // Clear local storage
    localStorage.removeItem('affiliate_session');
    localStorage.removeItem('affiliate_tracking_token');
    localStorage.removeItem('affiliate_id');

    // Clear session storage
    sessionStorage.removeItem('affiliate_session');
    sessionStorage.removeItem('affiliate_tracking_token');
    sessionStorage.removeItem('affiliate_id');

    // Disable testing mode
    setTestingMode(false);

    console.log('✅ All affiliate data cleared successfully');
    console.log('🧪 You can now start fresh with new testing');
  } catch (error) {
    console.error('❌ Error clearing affiliate data:', error);
  }
};

// Debug affiliate session
export const debugAffiliateSession = (): void => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    console.log('🚫 Testing functions are only available in development mode');
    return;
  }

  try {
    console.log('🧪 Debugging Affiliate Session:');
    console.log('  ================================');

    // Check localStorage
    console.log('📦 LocalStorage:');
    console.log('  - affiliate_session:', localStorage.getItem('affiliate_session'));
    console.log('  - affiliate_tracking_token:', localStorage.getItem('affiliate_tracking_token'));
    console.log('  - affiliate_id:', localStorage.getItem('affiliate_id'));

    // Check sessionStorage
    console.log('📦 SessionStorage:');
    console.log('  - affiliate_session:', sessionStorage.getItem('affiliate_session'));
    console.log('  - affiliate_tracking_token:', sessionStorage.getItem('affiliate_tracking_token'));
    console.log('  - affiliate_id:', sessionStorage.getItem('affiliate_id'));

    // Check secure session
    const session = getSecureAffiliateSession();
    console.log('🔐 Secure Session:');
    console.log('  - Session:', session);

    // Check testing mode
    console.log('🧪 Testing Mode:', isInTestingMode() ? 'ENABLED' : 'DISABLED');

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔗 URL Parameters:');
    console.log('  - partner:', urlParams.get('partner'));
    console.log('  - affiliate:', urlParams.get('affiliate'));

    console.log('  ================================');
  } catch (error) {
    console.error('❌ Error debugging affiliate session:', error);
  }
};

// List all affiliates
export const listAllAffiliates = async (): Promise<void> => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    console.log('🚫 Testing functions are only available in development mode');
    return;
  }

  try {
    console.log('🧪 Fetching all affiliates...');

    const affiliatesRef = collection(db, 'affiliates');
    const affiliatesSnapshot = await getDocs(affiliatesRef);

    if (affiliatesSnapshot.empty) {
      console.log('📋 No affiliates found in database');
      return;
    }

    console.log(`📋 Found ${affiliatesSnapshot.size} affiliates:`);
    console.log('  ================================');

    affiliatesSnapshot.forEach((doc) => {
      const affiliate = doc.data();
      console.log(`  🔗 ID: ${doc.id}`);
      console.log(`     Name: ${affiliate.name || 'N/A'}`);
      console.log(`     Email: ${affiliate.email || 'N/A'}`);
      console.log(`     Code: ${affiliate.affiliateCode || 'N/A'}`);
      console.log(`     Status: ${affiliate.status || 'N/A'}`);
      console.log(`     Total Earnings: $${affiliate.totalEarnings || 0}`);
      console.log(`     Created: ${affiliate.createdAt ? new Date(affiliate.createdAt.toDate()).toLocaleString() : 'N/A'}`);
      console.log('  --------------------------------');
    });
  } catch (error) {
    console.error('Error fetching affiliates:', error);
  }
};

// Get affiliate code by ID
export const getAffiliateCodeById = async (affiliateId: string): Promise<void> => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    const affiliateDoc = await getDoc(affiliateRef);

    if (!affiliateDoc.exists()) {
      return;
    }
  } catch (error) {
    console.error('Error fetching affiliate details:', error);
  }
};

// Debug affiliate earnings calculation
export const debugAffiliateEarnings = async (affiliateId: string): Promise<void> => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    const stats = await getAffiliateStats(affiliateId);
    if (stats) {
      // Get all conversions for this affiliate
      const conversionsQuery = query(
        collection(db, 'affiliate_conversions'),
        where('affiliateId', '==', affiliateId)
      );
      const conversionsSnapshot = await getDocs(conversionsQuery);
      const allConversions = conversionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AffiliateConversion[];

      // Calculate earnings manually
      const approvedConversions = allConversions.filter(conv =>
        conv.status === 'approved' || conv.status === 'paid'
      );
      const manualTotal = approvedConversions.reduce((sum, conv) => sum + conv.commission, 0);

      if (Math.abs(manualTotal - stats.totalEarnings) > 0.01) {
        console.warn('Earnings mismatch detected for affiliate:', affiliateId);
      }
    }
  } catch (error) {
    console.error('Error debugging earnings:', error);
  }
};

// Sync affiliate earnings (admin function)
export const syncAffiliateEarningsConsole = async (affiliateId: string): Promise<void> => {
  // Only allow in development mode
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    await syncAffiliateEarnings(affiliateId);
  } catch (error) {
    console.error('Error syncing earnings:', error);
  }
};

// Initialize global testing functions (development only)
if (process.env.NODE_ENV === 'development') {
  // Make functions available globally for console testing
  (window as any).createSessionForCurrentAffiliate = createSessionForCurrentAffiliate;
  (window as any).clearTestingSession = clearTestingSession;
  (window as any).checkTestingStatus = checkTestingStatus;
  (window as any).clearAllAffiliateData = clearAllAffiliateData;
  (window as any).debugAffiliateSession = debugAffiliateSession;
  (window as any).listAllAffiliates = listAllAffiliates;
  (window as any).getAffiliateCodeById = getAffiliateCodeById;
  (window as any).debugAffiliateEarnings = debugAffiliateEarnings;
  (window as any).syncAffiliateEarningsConsole = syncAffiliateEarningsConsole;
}



